import { Head, router } from "@inertiajs/react"
import { useState, useCallback, type MouseEvent } from "react"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"

interface Job {
  id: number
  class_name: string
  queue_name: string
  arguments: string
  priority: number
  scheduled_at: string | null
  status: string
  created_at: string
  finished_at: string | null
  error?: string
}

interface WorkerProcess {
  id: number
  kind: string
  hostname: string
  pid: number
  last_heartbeat_at: string
  name: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  tab: string
  search: string | null
  sort: string
  direction: string
  jobs: Job[]
  pagination: PaginationMeta
  counts: {
    all: number
    failed: number
    scheduled: number
    in_progress: number
    completed: number
  }
  processes: WorkerProcess[]
}

const TABS = [
  { value: "all", label: "All" },
  { value: "failed", label: "Failed" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
] as const

const STATUS_COLOURS: Record<string, string> = {
  failed: "bg-red/10 text-red border-red/20",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  in_progress: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  pending: "bg-cream/10 text-cream/60 border-cream/20",
}

const COLUMNS = [
  { key: "id", label: "ID", sortable: true },
  { key: "class_name", label: "Class", sortable: true },
  { key: "queue_name", label: "Queue", sortable: true },
  { key: "status", label: "Status", sortable: false },
  { key: "created_at", label: "Created", sortable: true },
  { key: "finished_at", label: "Finished", sortable: true },
] as const

export default function AdminJobsIndex({ tab, search, sort, direction, jobs, pagination, counts, processes }: Props) {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [searchValue, setSearchValue] = useState(search || "")

  const buildParams = useCallback(
    (overrides: Record<string, string | undefined> = {}) => {
      const p: Record<string, string> = { tab }
      if (search) p.search = search
      if (sort !== "created_at") p.sort = sort
      if (direction !== "desc") p.direction = direction
      const filtered = Object.entries(overrides).filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== "")
      return { ...p, ...Object.fromEntries(filtered) }
    },
    [tab, search, sort, direction],
  )

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/jobs", buildParams({ search: searchValue || undefined, page: undefined }), { preserveState: true })
  }

  function handleSort(column: string) {
    const newDirection = sort === column && direction === "asc" ? "desc" : "asc"
    router.get("/admin/jobs", buildParams({ sort: column, direction: newDirection, page: undefined }), { preserveState: true })
  }

  function handleRowClick(e: MouseEvent, job: Job) {
    e.preventDefault()
    e.stopPropagation()
    setSelectedJob(job)
  }

  return (
    <>
      <Head title="Jobs | Admin" />
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-display font-semibold text-cream">Jobs</h1>
            <p className="mt-1 text-sm text-cream/50">Solid Queue monitoring</p>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Mobile: Select dropdown */}
            <div className="sm:hidden w-full">
              <Select
                value={tab}
                onValueChange={(value) => router.get("/admin/jobs", { tab: value, search: searchValue || undefined }, { preserveState: true })}
              >
                <SelectTrigger className="w-full border-white/10 bg-white/5 text-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark border-white/10 bg-charcoal">
                  {TABS.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label} ({counts[value as keyof typeof counts]})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Desktop: Tabs */}
            <Tabs
              value={tab}
              onValueChange={(value) => router.get("/admin/jobs", { tab: value, search: searchValue || undefined }, { preserveState: true })}
              className="hidden sm:block"
            >
              <TabsList>
                {TABS.map(({ value, label }) => (
                  <TabsTrigger key={value} value={value} className="gap-1.5">
                    {label}
                    <span className="ml-0.5 text-[10px] tabular-nums opacity-50">
                      {counts[value as keyof typeof counts]}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-cream/30" />
                <Input
                  placeholder="Search class or queue..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="h-8 w-56 border-white/10 bg-white/5 pl-8 text-sm text-cream placeholder:text-cream/30"
                />
              </div>
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-cream/50 hover:text-cream"
                  onClick={() => {
                    setSearchValue("")
                    router.get("/admin/jobs", { tab }, { preserveState: true })
                  }}
                >
                  Clear
                </Button>
              )}
            </form>
          </div>

          {/* Jobs table */}
          <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">
                      {col.sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 hover:text-cream/70 transition-colors"
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                          <SortIcon active={sort === col.key} direction={sort === col.key ? direction : undefined} />
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-cream/30">
                      {search ? `No jobs matching "${search}"` : "No jobs found"}
                    </td>
                  </tr>
                )}
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    className="border-b border-white/[0.04] text-cream/70 transition-colors hover:bg-white/[0.04] cursor-pointer"
                    onClick={(e) => handleRowClick(e, job)}
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-cream/40">{job.id}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs">{job.class_name}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-cream/50">{job.queue_name}</td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <Badge className={STATUS_COLOURS[job.status] || STATUS_COLOURS.pending}>
                        {job.status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-cream/50">{formatDate(job.created_at)}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-cream/50">{job.finished_at ? formatDate(job.finished_at) : "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            baseUrl="/admin/jobs"
            params={buildParams()}
            pagination={pagination}
          />

          {/* Worker processes */}
          {processes.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-medium uppercase tracking-wider text-cream/40">Worker processes</h2>
              <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
                <table className="w-full min-w-[480px] text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                      <th className="whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Name</th>
                      <th className="whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Kind</th>
                      <th className="whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Host</th>
                      <th className="whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">PID</th>
                      <th className="whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Heartbeat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processes.map((proc) => (
                      <tr key={proc.id} className="border-b border-white/[0.04] text-cream/60">
                        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs">{proc.name}</td>
                        <td className="whitespace-nowrap px-4 py-2.5">{proc.kind}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-cream/40">{proc.hostname}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-cream/40">{proc.pid}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs text-cream/40">{formatDate(proc.last_heartbeat_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </AdminLayout>

      {/* Job detail modal — portals to body, outside AdminLayout tree */}
      <Dialog open={!!selectedJob} onOpenChange={(open) => { if (!open) setSelectedJob(null) }}>
        <DialogContent className="dark max-h-[85vh] overflow-y-auto border-white/10 bg-charcoal sm:max-w-lg [&>*]:min-w-0">
          {selectedJob && (
            <JobDetailContent
              job={selectedJob}
              onClose={() => setSelectedJob(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

function JobDetailContent({ job, onClose }: { job: Job; onClose: () => void }) {
  let parsedArgs: string
  try {
    const args = typeof job.arguments === "string" ? JSON.parse(job.arguments) : job.arguments
    parsedArgs = JSON.stringify(args, null, 2)
  } catch {
    parsedArgs = String(job.arguments)
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3 text-cream">
          <span className="font-mono text-sm text-cream/40">#{job.id}</span>
          <Badge className={STATUS_COLOURS[job.status] || STATUS_COLOURS.pending}>
            {job.status.replace("_", " ")}
          </Badge>
        </DialogTitle>
        <DialogDescription className="font-mono text-xs text-cream/60">
          {job.class_name}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-2.5 text-sm">
        <DetailRow label="Queue" value={job.queue_name} />
        <DetailRow label="Priority" value={String(job.priority)} />
        <DetailRow label="Created" value={formatDateFull(job.created_at)} />
        <DetailRow label="Scheduled" value={job.scheduled_at ? formatDateFull(job.scheduled_at) : "\u2014"} />
        <DetailRow label="Finished" value={job.finished_at ? formatDateFull(job.finished_at) : "\u2014"} />
      </div>

      <div className="min-w-0 space-y-1.5">
        <p className="text-xs font-medium uppercase tracking-wider text-cream/40">Arguments</p>
        <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-black/20 p-3 text-xs leading-relaxed text-cream/70">{parsedArgs}</pre>
      </div>

      {job.error && (
        <div className="min-w-0 space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wider text-red/70">Error</p>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md border border-red/10 bg-red/5 p-3 text-xs leading-relaxed text-cream/70">{job.error}</pre>
        </div>
      )}

      {job.status === "failed" && (
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="border-white/20 text-cream hover:bg-white/10"
            onClick={() => {
              router.post(`/admin/jobs/${job.id}/retry`, {}, { onSuccess: onClose })
            }}
          >
            Retry
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-red/20 text-red hover:bg-red/10"
            onClick={() => {
              router.post(`/admin/jobs/${job.id}/discard`, {}, { onSuccess: onClose })
            }}
          >
            Discard
          </Button>
        </DialogFooter>
      )}
    </>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-8">
      <span className="text-cream/40">{label}</span>
      <span className="text-cream/80 text-right">{value}</span>
    </div>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg className="size-3 opacity-30" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  )
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatDateFull(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
